import { GridStack, type GridStackWidget } from "gridstack";
import "gridstack/dist/gridstack-extra.css";
import "gridstack/dist/gridstack.css";

import type { IUpdateServiceOrderCmd } from "@/data/commands/room/UpdateServiceOrderCmd";
import { Factory } from "@/data/dao/Factory";
import { RoomDao } from "@/data/dao/service/RoomDao";
import { ServiceAvailableFor, ServiceType } from "@/data/model/Enums";
import type { IRoom } from "@/data/model/Room";
import type { IService } from "@/data/model/Service";
import { Format } from "@/data/util/Format";

import { translate } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { ServiceModal } from "@/portal/modal/ServiceModal";
import { UiNotification } from "@/portal/ui/Notification";

export class ServiceEditor {
    private $element: JQuery;
    private $editor: JQuery;
    private $addGrid: JQuery;
    private $stack: JQuery;

    private room: IRoom;
    private availableFor: ServiceAvailableFor;
    private roomDao: RoomDao;
    private grid!: GridStack;

    private cols = 2;
    private rows = 3;
    private isDisabled = false;

    constructor(room: IRoom, availableFor: ServiceAvailableFor) {
        this.room = room;
        this.availableFor = availableFor;

        this.roomDao = Factory.getInstance(RoomDao);
        this.$element = $('<div class="serviceeditor" />');

        this.$editor = $('<div class="editor " />').appendTo(this.$element);
        this.$addGrid = $('<div class="add" />').appendTo(this.$editor);
        this.$stack = $('<div class="grid-stack" />').appendTo(this.$editor);

        const pWidth = 100 / this.cols;
        const pHeight = 100 / this.rows;

        for (let row = 0; row < this.rows; row++) {
            for (let column = 0; column < this.cols; column++) {
                $(
                    `<div class="addhandler" data-x="${column}" data-y="${row}">
                        <div class="centered">
                            <i class="flaticon-add-circular-button"></i>
                            <div class="lbl">${i18n("room.addService")}</div>
                        </div>
                    </div>`
                )
                    .css({
                        width: pWidth + "%",
                        height: pHeight + "%",
                        top: row * pHeight + "%",
                        left: column * pWidth + "%",
                    })
                    .appendTo(this.$addGrid)
                    .click((e) => {
                        if (this.isDisabled) return;

                        const target = $(e.currentTarget);
                        const x = parseInt(target.attr("data-x"));
                        const y = parseInt(target.attr("data-y"));

                        this.onAddService(x + y * this.cols);
                    });
            }
        }
    }

    private async init() {
        this.grid = GridStack.init(
            {
                column: this.cols,
                row: this.rows,
                minRow: this.rows,
                maxRow: this.rows,
                margin: 8,
                cellHeight: "145px",
                float: true,
                animate: true,
                disableResize: true,
                handle: ".handle",
            },
            this.$stack[0]
        );

        await this.loadServices();

        this.grid.on("dragstart", () => {
            this.$addGrid.hide();
        });

        this.grid.on("dragstop", () => {
            this.$addGrid.show();
        });

        this.grid.on("change", (event: Event, items: any) => {
            const newOrders = new Array<{ serviceId: string; order: number }>();
            items.forEach((item) => {
                newOrders.push({
                    serviceId: $(item.el).data("model").id,
                    order: item.y * this.cols + item.x,
                });
            });

            this.onServicesReordered(newOrders);
            this.updateAddHandlers();
        });
    }

    public async loadServices() {
        try {
            const services = await this.roomDao.getServices(this.room.id, { availableFor: this.availableFor });
            for (const service of services) {
                this.addService(service);
            }
            this.updateAddHandlers();
        } catch (ex) {
            logError(ex);
            UiNotification.showException(i18n("room.failedLoadingServices"));
        }
    }

    public appendTo($element: JQuery): ServiceEditor | undefined {
        $element.append(this.$element);
        if (this.grid !== undefined) return undefined;

        window.setTimeout(() => {
            this.init();
        }, 100);

        return this;
    }

    private addService(m: IService) {
        const col = Math.ceil((m.order + 1) / this.cols);
        const pos = {
            x: (m.order + 1) % this.cols === 0 ? 1 : 0,
            y: col - 1,
        } as GridStackWidget;

        let name: string = translate(m.name);
        if (name === undefined) name = i18n("enums.ServiceType." + ServiceType[m.serviceType]);

        const widgetContent = `
            <div>
                <div class="grid-stack-item-content">
                    <div class="handle ui-draggable-handle">
                        <i class="la la-bars"></i>
                    </div>
                    <div class="inner">
                        <div class="centered">
                            <div class="lbl">${Format.htmlEscape(name)}</div>
                        </div>
                    </div>
                </div>
            </div>
        `;

        const wdg = this.grid.addWidget(widgetContent, pos);

        const container = $(wdg).data("model", m).find("> .grid-stack-item-content");
        container.find("> .inner").click((e) => {
            if (this.isDisabled) return;

            this.onEditService($(e.currentTarget).parent().parent().data("model"));
        });
    }

    private updateService(model: IService) {
        const el = this.elementByModel(model);
        if (el.length === undefined) return;

        let name = translate(model.name);
        if (name === undefined) name = i18n("enums.ServiceType." + ServiceType[model.serviceType]);

        el.data("model", model).find("> .grid-stack-item-content > .inner > .centered > .lbl").html(name);
    }

    private elementByModel(model: IService): JQuery {
        let found: JQuery;
        this.$stack.find("> .grid-stack-item").each((i, e) => {
            const el = $(e);
            if (el.data("model").id === model.id) {
                found = el;
                return false;
            }
        });

        return found!;
    }

    private onEditService(model: IService) {
        const m = new ServiceModal(this.room.locationId, this.room.id, model);
        m.onRemoved = () => {
            const el = this.elementByModel(model);
            if (el !== undefined) {
                this.grid.removeWidget(el[0], true, false);
                this.updateAddHandlers();
            }
        };

        m.onSaved = (m) => {
            this.updateService(m);
        };

        m.show();
    }

    private onAddService(addOrder: number) {
        const takenServices = new Array<ServiceType>();
        this.$stack.find("> .grid-stack-item").each((i, e) => {
            takenServices.push($(e).data("model").serviceType);
        });

        const modal = new ServiceModal(
            this.room.locationId,
            this.room.id,
            undefined,
            addOrder,
            takenServices,
            this.availableFor
        );
        modal.onSaved = (service) => {
            this.addService(service);
            this.updateAddHandlers();
        };

        modal.show();
    }

    private updateAddHandlers() {
        const currentServices = this.grid.getGridItems();
        this.$addGrid.children(".addhandler").each((i, e) => {
            const el = $(e);
            const x = el.attr("data-x");
            const y = el.attr("data-y");

            if (this.serviceWithCoordsExists(currentServices, x, y)) {
                el.hide();
            } else {
                el.show();
            }
        });
    }

    private serviceWithCoordsExists(currentServices, x: string, y: string) {
        return currentServices.find((c) => c.getAttribute("gs-x") == x && c.getAttribute("gs-y") == y);
    }

    private async onServicesReordered(patachbleOrders: Array<{ serviceId: string; order: number }>) {
        this.setDisabled(true);

        const cmd: IUpdateServiceOrderCmd = {
            newOrders: patachbleOrders,
        };
        console.error("services reordered");

        try {
            await this.roomDao.updateServiceOrder(this.room.id, cmd);
            this.setDisabled(false);
        } catch (ex) {
            logError(ex);
            UiNotification.showException(i18n("room.failedChangingOrder"));
        }
    }

    private setDisabled(isDisabled: boolean) {
        if (this.isDisabled === isDisabled) return;

        this.isDisabled = isDisabled;
        if (this.isDisabled) {
            this.grid.disable();
        } else {
            this.grid.enableMove(true);
        }
    }
}
